import { useEffect } from 'react';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import BaseModal from '@/components/common/BaseModal';
import { OSM_RASTER_TILE_URL, OSM_TILE_ATTRIBUTION } from '@/utils/mapTiles';
import 'leaflet/dist/leaflet.css';
import '@/utils/leaflet-fix';

function FitEvent({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    const id = window.setTimeout(() => map.invalidateSize(), 80);
    return () => window.clearTimeout(id);
  }, [map]);
  useEffect(() => {
    map.setView(center, 16);
  }, [map, center]);
  return null;
}

export function EventLocationMapModal({
  title,
  subtitle,
  lat,
  lng,
  occurredAt,
  address,
  onClose,
}: {
  title: string;
  subtitle?: string;
  lat: number;
  lng: number;
  occurredAt?: string | null;
  address?: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const center: [number, number] = [lat, lng];
  const when = occurredAt
    ? new Date(occurredAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
    : null;

  return (
    <BaseModal isOpen onClose={onClose} title={title} subtitle={subtitle} size="2xl">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-textDefault">
          <span className="inline-flex items-center gap-1 font-medium">
            <MapPinIcon className="h-4 w-4 text-primary" />
            {t('unplugEvents.mapSource')}
          </span>
          {when && (
            <span className="text-textSecondary">
              {t('unplugEvents.occurredAt')}: {when}
            </span>
          )}
        </div>
        {address && <p className="text-sm text-textSecondary">{address}</p>}
        <div className="h-[min(480px,60vh)] w-full overflow-hidden rounded-lg border border-border">
          <MapContainer center={center} zoom={16} className="z-0 h-full w-full" scrollWheelZoom>
            <TileLayer url={OSM_RASTER_TILE_URL} attribution={OSM_TILE_ATTRIBUTION} />
            <Marker position={center} />
            <FitEvent center={center} />
          </MapContainer>
        </div>
      </div>
    </BaseModal>
  );
}
